import {Component, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import * as L from 'leaflet';
import "beautifymarker/leaflet-beautify-marker-icon";
import {RedrawMapEvent} from '@events/RedrawMapEvent';
import {LatLongEvent} from '@events/LatLongEvent';
import "../../../lib/leaflet.SimpleIcon";
import {ThingTypeEvent} from '@events/things/ThingTypeEvent';
import {ThingsUtil} from '../../../util/things';
import {Subscription} from "rxjs/Subscription";

const WDW_BOUNDS = {topLeft: {lat: 28.5305, lon: -81.733378}, bottomRight: {lat: 28.2647, lon: -81.279553}};
const ZOOM = 19;
const BOUNDS = new L.LatLngBounds(
  new L.LatLng(WDW_BOUNDS.topLeft.lat, WDW_BOUNDS.topLeft.lon),
  new L.LatLng(WDW_BOUNDS.bottomRight.lat, WDW_BOUNDS.bottomRight.lon)
);

const MAX_ZOOM = 19;
const MIN_EDIT_ZOOM = 12;

const POPUP_OPTIONS = {
  offset: [0, -46],
  closeButton: false,
  className: 'map-popup'
};

@Component({
  selector: 'things-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.less'],
  encapsulation: ViewEncapsulation.None // Without this styling wasn't being applied to markers
})

export class ThingsMapComponent implements OnInit, OnDestroy{

  @Input()
  public thing;

  public leafletMap;

  @Input()
  public mode;

  @Input()
  public mapId;

  @Input()
  public lat;

  @Input()
  public lon;

  public simpleIcon;
  public previewMarker;

  @Input()
  public thingType;

  public previewThing;

  redrawSubscription: Subscription;
  panSubscription: Subscription;
  thingTypeSubscription: Subscription;
  latLongSubscription: Subscription;

  constructor(private redrawMap: RedrawMapEvent, private latLongEvent: LatLongEvent, private thingTypeEvent: ThingTypeEvent, private thingsUtil: ThingsUtil) {}

  /**
   * Called after the map div has been registered in the document DOM
   */
  public ngAfterViewInit(): void {
    // console.log("ngAfterViewInit(): " + this.mapId );
    this.initMap();
  }

  initMap() {
    // I am unsure about the && false.  this is probably just to diable zoom
    // const MIN_ZOOM = this.mode === 'edit' && false ? MIN_EDIT_ZOOM : MAX_ZOOM;
    const MIN_ZOOM = MAX_ZOOM;

    let tilesLayer = new L.LayerGroup();

    this.leafletMap = L.map(this.mapId, {
      zoomControl: false,
      dragging: this.mode === 'add' || this.mode === 'edit'
    }).setView([
      this.thing.lat || this.lat,
      this.thing.lon || this.lon,
    ], ZOOM);

    L.tileLayer('https://cdn6.parksmedia.wdprapps.disney.com/media/maps/prod/wdw/144/{z}/{x}/{y}.jpg', {
      maxBounds: BOUNDS,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM
    }).addTo(tilesLayer);

    tilesLayer.addTo(this.leafletMap);

    if (this.thing.lat && this.thing.lon) {
      this.simpleIcon = this.getMarker(this.thing.lat, this.thing.lon, this.mode != 'preview').addTo(this.leafletMap);

      if (this.mode === 'edit') {
        this.simpleIcon
          .bindPopup(this.constructPopupHtml(this.thing.lat, this.thing.lon, false), POPUP_OPTIONS)
          .openPopup();
      }
    }
    if (this.previewThing && this.previewThing.lat && this.previewThing.lon) {
      this.previewMarker = this.getPreviewMarker(this.previewThing.lat, this.previewThing.lon).addTo(this.leafletMap);
    }

    if (this.mode === 'add' || this.mode === 'edit') {
      this.leafletMap.on('click', e => {
        if (!this.previewThing) {
          this.previewThing = {};
        }

        this.previewThing.lat = e.latlng.lat;
        this.previewThing.lon = e.latlng.lng;
        this.lat = this.previewThing.lat;
        this.lon = this.previewThing.lon;

        this.leafletMap.panTo(new L.LatLng(e.latlng.lat, e.latlng.lng));
        this.latLongEvent.fire('thingsMap.update', e.latlng.lat, e.latlng.lng);

        if (!this.previewMarker) {
          this.previewMarker = this.getPreviewMarker(e.latlng.lat, e.latlng.lng);

          this.previewMarker
            .addTo(this.leafletMap)
            .bindPopup(this.constructPopupHtml(e.latlng.lat, e.latlng.lng, true), POPUP_OPTIONS)
            .openPopup();
        } else {
          this.previewMarker.setLatLng(new L.LatLng(e.latlng.lat, e.latlng.lng))
            .bindPopup(this.constructPopupHtml(e.latlng.lat, e.latlng.lng, true), POPUP_OPTIONS, )
            .openPopup();
        }
      }, this);
    }
  }

  refreshMap(){
    if( !this.leafletMap ){
      // console.log("Map doesn't exist for " + this.mapId + ", will skip refresh");
      return;
    }

    const ZOOM = 19;

    this.leafletMap
      .setView([
        this.thing.lat || this.lat,
        this.thing.lon || this.lon,
      ], ZOOM);

    if( this.simpleIcon ){
      this.leafletMap.removeLayer(this.simpleIcon);
    }
    if( this.thing && this.thing.lat && this.thing.lon ) {
      this.simpleIcon = this.getMarker(this.thing.lat, this.thing.lon).addTo(this.leafletMap);
    }

    if( this.previewMarker ){
      this.leafletMap.removeLayer(this.previewMarker);
    }

    if( this.previewThing ) {
      this.previewMarker = this.getPreviewMarker(this.previewThing.lat, this.previewThing.lon).addTo(this.leafletMap);
    }
  }

  panMap() {
    if (! this.leafletMap) {
      return;
    }

    const ZOOM = 19;

    this.leafletMap.setView([
      this.thing.lat || this.lat,
      this.thing.lon || this.lon
    ], ZOOM);
  }

  ngOnInit() {
    this.redrawSubscription = this.redrawMap.on().subscribe(data => {
      if (data.key === 'thingsDetailMap.redraw') {
        if (this.thing.thingId == data.thing.thingId) {
          // console.log("Refreshing map for: " + data.thing.thingId);
          this.thing = data.thing;
          this.refreshMap();
        }
      }
    });

    this.panSubscription = this.latLongEvent.on().subscribe(data => {
      if (data.key === 'thingsDetailMap.pan') {
        this.lat = data.lat;
        this.lon = data.lon;
        this.panMap();
      }
    });

    if (this.mode === 'add' || this.mode === 'edit') {
      this.thingTypeSubscription = this.thingTypeEvent.on().subscribe(data => {
        this.thing.type = data;
        this.refreshMap();
      });

      this.latLongSubscription = this.latLongEvent.on().subscribe(data => {
        if (data.key === 'thingsMap.addPreviewMarker') {
          this.leafletMap.panTo(new L.LatLng(data.lat, data.lon));

          if (!this.previewMarker) {
            this.previewMarker = this.getPreviewMarker(data.lat, data.lon);
            this.previewMarker.addTo(this.leafletMap);
          } else {
            this.previewMarker.setLatLng(new L.LatLng(data.lat, data.lon));
          }

          this.previewMarker
            .bindPopup(this.constructPopupHtml(data.lat, data.lon, true), POPUP_OPTIONS)
            .openPopup();
        }
      });
    }
  }

  ngOnDestroy() {
    this.destroy();
  }

  destroy() {
    if (this.leafletMap && this.leafletMap._containerId) {
      this.leafletMap.off();
      this.leafletMap.remove();
      this.leafletMap = undefined;
    }

    let container = document.getElementById(this.mapId);
    if (container) {
      container.innerHTML = '';
    }

    if( this.redrawSubscription ){
      // subscription still remain when destroyed, will remove.
      this.redrawSubscription.unsubscribe();
      this.redrawSubscription = undefined;
    }

    if (this.panSubscription ) {
      this.panSubscription.unsubscribe();
      this.panSubscription = undefined;
    }

    if (this.thingTypeSubscription ) {
      this.thingTypeSubscription.unsubscribe();
      this.thingTypeSubscription = undefined;
    }

    if (this.latLongSubscription ) {
      this.latLongSubscription.unsubscribe();
      this.latLongSubscription = undefined;
    }
  }

  getIconType() {
    let thing = (this.thing && this.thing.type) ? this.thing : { type: this.thingType };

    return this.thingsUtil.getIcon(thing);
  }

  getMarker(lat, lon, interactive = true) {
    return L.marker([lat, lon], {
      interactive: interactive,
      icon: L.SimpleIcon.icon({
        iconShape: 'marker',
        iconSize: [40,48],
        iconAnchor: [20, 48],
        popupAnchor: [0, 0],
        imageSrc: this.thingsUtil.isResource(this.thing) ? 'assets/images/ic_mapmarker-resource.svg' : '/assets/images/marker.png',
        textColor: this.thingsUtil.isResource(this.thing) ? '#ffffff' : '#263B55',
        backgroundColor: 'white',
        prefix: 'kng-icon',
        icon: this.getIconType(),
        borderStyle: 'none',
        top: this.thingsUtil.isResource(this.thing) ? '-38px' : '-40px;',
        left: this.thingsUtil.isResource(this.thing) ? '9px' : '10px'
        //html: '<i class="kng-icon kng-icon-' + this.getIconType() + '"></i>'
      })
    });
  }

  getPreviewMarker(lat, lon) {
    return L.marker([lat, lon], {
      icon: L.SimpleIcon.icon({
        iconShape: 'marker',
        iconSize: [40,48],
        iconAnchor: [20, 48],
        popupAnchor: [0, 0],
        imageSrc: '/assets/images/preview-marker.png',
        textColor: '#263B55',
        prefix: 'kng-icon',
        icon: this.getIconType(),
        borderStyle: 'none'
      })
    });
  }

  constructPopupHtml(lat, long, isPreview){
    let locationStatus = '';
    if (this.mode == 'add') {
      locationStatus = 'PLACE HERE';
    } else if (this.mode == 'edit') {
      locationStatus = isPreview ? 'MOVE HERE' : 'CURRENT';
    }

    return '<label class="lat-long-label b2">Latitude, Longitude</label>' +
      '<div class="lat-long-value b2 medium">' + lat + ',' + long + '</div>' +
      '<div class="location-status b3">' + locationStatus + '</div>'
  }
}



// WEBPACK FOOTER //
// D:/dclean-web-latest/cot-web/src/app/components/things/map/map.component.ts